/**
 * F2 Item 9 - Progression guide.
 *
 * Public discovery page for the RPG system:
 *   - Section 1: how points are earned (from POINT_RULES via API)
 *   - Section 2: the Dost rank ladder (all 10 ranks x subtitle + level range)
 *   - Section 3: achievements (link to profile)
 *
 * Warm editorial tone. Zero em/en-dashes. Middle-dot separators. Ember/navy
 * palette. Reduced motion honored.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Trophy, TrendingUp } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const ProgressionGuidePage = () => {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get('/progression/config')
      .then(r => setCfg(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Failed to load config'));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-10" data-testid="progression-guide-page">
      <header className="text-center space-y-2">
        <h1 className="font-display text-4xl md:text-5xl font-bold text-primary-900">
          How progression works
        </h1>
        <p className="text-neutral-600 max-w-2xl mx-auto">
          Dostination has a quiet, editorial way of tracking your journey. Show up, share your story, bring a friend. The community warms up. This is how the ranks and points fit together.
        </p>
      </header>

      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {err}
        </div>
      )}

      <Card data-testid="progression-guide-earning">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-secondary-600" />
            Earning points
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-neutral-600 mb-4">
            You earn points by doing the things that make Dostination itself work. Nothing spammable. Nothing that games the system. The heaviest reward comes from actually showing up and bringing others.
          </p>
          {!cfg ? (
            <div className="text-sm text-neutral-500">Loading...</div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {(cfg.point_rules || []).map(r => (
                <li
                  key={r.kind}
                  className="flex items-center justify-between py-2"
                  data-testid={`progression-guide-point-${r.kind}`}
                >
                  <span className="text-sm text-primary-900">{r.label}</span>
                  <Badge
                    variant="outline"
                    className="font-mono bg-secondary-50 text-secondary-800 border-secondary-200"
                  >
                    +{r.points}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card data-testid="progression-guide-ranks">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-secondary-600" />
            The Dost ranks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-neutral-600 mb-4">
            Ten ranks. From your first hello to the quiet ones who hold the community together. Each rank has a level range. Points build levels; levels build rank.
          </p>
          {!cfg ? (
            <div className="text-sm text-neutral-500">Loading...</div>
          ) : (
            <ol className="space-y-3">
              {(cfg.ranks || []).map(r => (
                <li
                  key={r.rank_index}
                  className="flex items-start gap-4 rounded-lg border border-neutral-100 p-3"
                  data-testid={`progression-guide-rank-${r.rank_index}`}
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center font-display font-bold text-secondary-800 shrink-0">
                    {r.rank_index}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-primary-900">
                      {r.name}
                      {r.subtitle ? (
                        <span className="text-neutral-500 font-normal ml-2 text-sm">
                          · {r.subtitle}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 font-mono">
                      Levels {r.level_min} to {r.level_max}
                    </div>
                    {r.description ? (
                      <div className="text-xs text-neutral-600 mt-1">{r.description}</div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <Card data-testid="progression-guide-achievements">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="h-5 w-5 text-secondary-600" />
            Achievements
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-neutral-700">
          <p>
            Achievements are quiet acknowledgments for the things worth remembering. Showing up for your first gathering. Bringing three friends who came back for more. A whole travel arc. The system watches gently and marks the moments.
          </p>
          <p className="text-neutral-500 text-xs">
            Most unlock as you go. A few are secret. There are no leaderboards; the collection is yours to build at your pace.
          </p>
          <Link
            to="/profile#achievements"
            data-testid="progression-guide-see-mine"
            className="inline-block mt-1 text-secondary-700 font-semibold hover:underline"
          >
            See your achievements
          </Link>
        </CardContent>
      </Card>

      {!user && (
        <div
          className="rounded-xl border border-secondary-200 bg-gradient-to-br from-secondary-50 to-white p-6 text-center"
          data-testid="progression-guide-signup-cta"
        >
          <p className="text-primary-900 font-medium">
            Not a Dost yet? Join and start earning.
          </p>
          <Link
            to="/auth/register"
            className="inline-block mt-3 px-4 py-2 rounded-full bg-secondary-500 text-primary-900 font-semibold text-sm hover:bg-secondary-400 transition-colors"
          >
            Create your account
          </Link>
        </div>
      )}
    </div>
  );
};

export default ProgressionGuidePage;
